import React, { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom";
import styles from './Login.module.scss'
import { useAuth } from '../contexts/AuthContext';
import eventBus from "../utils/EventBus";

export default function ResetPassword({ email, justSent = false }) {
    const codeRef = useRef();
    const newPasswordRef = useRef();
    const confirmPasswordRef = useRef();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [cooldown, setCooldown] = useState(justSent ? 30 : 0);
    const { resetPassword, forgotPassword } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setInterval(() => {
            setCooldown((c) => Math.max(0, c - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [cooldown]);

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        if (newPasswordRef.current.value !== confirmPasswordRef.current.value) {
            return setError('Passwords do not match');
        }

        setLoading(true);

        try {
            const result = await resetPassword(email, codeRef.current.value.trim(), newPasswordRef.current.value);

            if (!result.success) {
                setError(result.error);
            } else {
                eventBus.publish('modal', null);
                navigate('/dashboard');
            }
        } catch (err) {
            setError('Failed to reset password');
        } finally {
            setLoading(false);
        }
    }

    async function handleResend() {
        setError('');
        setResendLoading(true);

        try {
            const result = await forgotPassword(email);
            if (!result.success) {
                setError(result.error);
                setCooldown(result.retryAfterSeconds ?? 30);
            } else {
                setCooldown(30);
            }
        } catch (err) {
            setError('Failed to resend code');
        } finally {
            setResendLoading(false);
        }
    }

    return (
        <div className={styles.login}>
            <form className={styles.form} onSubmit={handleSubmit}>
                <div>
                    <h3 className={styles.title}>Reset your password</h3>
                    {error && <div className={styles.error}>{error}</div>}
                    <p>Enter the 6-digit code sent to <strong>{email}</strong> and choose a new password. The code expires in 10 minutes.</p>
                    <label>Reset Code</label>
                    <input
                        ref={codeRef}
                        type="text"
                        inputMode="numeric"
                        pattern="\d{6}"
                        maxLength={6}
                        placeholder="123456"
                        autoFocus
                        required
                    />
                    <label>New Password</label>
                    <input ref={newPasswordRef} type="password" placeholder="Enter New Password" required/>
                    <label>Confirm New Password</label>
                    <input ref={confirmPasswordRef} type="password" placeholder="Confirm New Password" required/>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Resetting...' : 'Reset password'}
                    </button>
                    <div className={styles.registerLink}>
                        Didn't get a code?{' '}
                        <button
                            type="button"
                            onClick={handleResend}
                            className={styles.linkButton}
                            disabled={cooldown > 0 || resendLoading}
                        >
                            {cooldown > 0 ? `Resend code (${cooldown}s)` : resendLoading ? 'Sending...' : 'Resend code'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    )
}
